// WHAT: Circular progress visualization component for MiracleFlow session completion rate.
// WHY:  Provides visual representation of overall session completion rate using a circular progress indicator with motivational messages.
// DOMAIN: Productivity → MiracleFlow
// DEPENDENCIES: React, @/components/ui, lucide-react, @tracq/frontend-shared/utils
// RISKS:
//   - WARN: Completion rate is clamped to 0-100; values outside this range are normalized, which may hide data issues.
//   - NOTE: Motivational messages are hardcoded based on thresholds (80%, 50%); may not match user expectations.
//   - PERF: Rendering a single progress circle is minimal; performance impact is negligible.

import { Target, CheckCircle } from "lucide-react";
import React from "react";

import { Card, Stack, Heading, ProgressCircle } from "@/components/ui";
import { cn } from "@/shared/utils";

interface ProgressVisualizationProperties {
  className?: string;
  completionRate: number;
}

/**
 * WHAT:
 *   Circular progress visualization component for MiracleFlow session completion rate.
 *
 * WHY:
 *   Provides users with visual representation of overall session completion rate using a circular
 *   progress indicator. Includes motivational messages based on performance thresholds to encourage
 *   continued engagement.
 *
 * PROPS:
 *   - completionRate: number - Completion rate percentage (0-100)
 *   - className?: string - Optional additional CSS classes
 *
 * BEHAVIOR:
 *   - Validates and clamps completionRate to valid range (0-100)
 *   - Displays circular progress indicator with percentage value
 *   - Shows motivational message based on completion rate:
 *     - >= 80%: "Excellent progress!" (green)
 *     - >= 50%: "Keep up the good work!" (blue)
 *     - < 50%: "You can do better!" (orange)
 *   - Applies color-coded progress states (success >= 80%, default >= 50%, warning < 50%)
 *   - Centers percentage value in progress circle
 *
 * RISKS:
 *   - WARN: Completion rate is clamped to 0-100; values outside this range are normalized, which may hide data issues.
 *   - NOTE: Motivational messages are hardcoded based on thresholds (80%, 50%); may not match user expectations.
 *   - PERF: Rendering a single progress circle is minimal; performance impact is negligible.
 *   - NOTE: ProgressCircle component size is fixed (w-32 h-32); may not scale well on smaller screens.
 *
 * @param root0
 *
 * @param root0.completionRate
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const ProgressVisualization: React.FC<ProgressVisualizationProperties> = ({
  className,
  completionRate,
}) => {
  // NOTE: Validate and clamp completionRate to valid range (0-100)
  const validCompletionRate =
    typeof completionRate === "number" && !Number.isNaN(completionRate)
      ? Math.max(0, Math.min(100, completionRate))
      : 0;

  return (
    <Card
      className={cn(
        "bg-[#1E293B] border-[#334155]",
        "hover:border-tracq-bright-blue/50 transition-all duration-300",
        "hover:shadow-tracq-card",
        className,
      )}
      padding="lg"
      variant="default"
    >
      <Stack align="center" direction="col" gap={4}>
        <Stack align="center" direction="row" gap={2}>
          <Target className="w-5 h-5 text-tracq-bright-blue" />
          <Heading className="text-[#F1F5F9] font-semibold" level="h3" size="md">
            Completion Rate
          </Heading>
        </Stack>

        <div className="relative w-32 h-32 mx-auto">
          <ProgressCircle
            max={100}
            size="lg"
            state={(() => {
              if (validCompletionRate >= 80) {
                return "success";
              }
              if (validCompletionRate >= 50) {
                return "default";
              }
              return "warning";
            })()}
            value={validCompletionRate}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#F1F5F9]">
                {Math.round(validCompletionRate)}%
              </div>
              <div className="text-xs text-[#94A3B8] mt-1">Success</div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-[#94A3B8] text-sm">
          {(() => {
            if (validCompletionRate >= 80) {
              return (
                <>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Excellent progress!</span>
                </>
              );
            }
            if (validCompletionRate >= 50) {
              return (
                <>
                  <Target className="w-4 h-4 text-tracq-bright-blue" />
                  <span>Keep up the good work!</span>
                </>
              );
            }
            return (
              <>
                <Target className="w-4 h-4 text-tracq-deep-orange" />
                <span>You can do better!</span>
              </>
            );
          })()}
        </div>
      </Stack>
    </Card>
  );
};
